import React from 'react';
import {
  ArrowDown,
  ArrowDownRight,
  ArrowRight,
  ArrowUpLeft,
  ArrowUpRight,
  Banknote,
  BookOpen,
  BriefcaseBusiness,
  Calendar,
  CalendarClock,
  ChartLine,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Download,
  Facebook,
  HandCoins,
  HeartHandshake,
  House,
  Info,
  Lightbulb,
  Link,
  Linkedin,
  List,
  LucideProps,
  Mail,
  MailCheck,
  MapPin,
  Menu,
  Mic,
  PenTool,
  Phone,
  Play,
  Pyramid,
  Search,
  Shield,
  Smartphone,
  Smile,
  Twitter,
  Upload,
  User,
  Users,
  UsersRound,
  Warehouse,
  Webcam,
  X,
  Zap,
  ArrowLeft,
  Instagram,
  RefreshCw,
  Newspaper,
  Video,
  Pen,
  ChevronUp,
  UserSquareIcon,
  GraduationCap,
  CircleCheck,
  CircleX,
  RotateCcw,
  Dot,
  Podcast,
  File,
  Star,
  Plus,
  Minus,
  Gift,
  Copy,
  Layers3,
  Flag,
} from 'lucide-react';
import { IconProps } from '@/components/icon/types';
import {
  LegendBoxIcon,
  LegendLineIcon,
  LegendPinIcon,
  TiktokIcon,
  WhatsappIcon,
  YoutubeIcon,
} from '@/components/icon/custom-icons';
import { renameDomProps } from '@/utils/sanitizeDomProps';

// Single source of truth for the supported icon set. `IconName` is derived from
// these keys below, so adding an icon means adding one entry here — no separate
// union to keep in sync. `satisfies` still type-checks every value as a
// component while preserving the literal key union for `IconName`.
const staticIcons = {
  'pen-tool': PenTool,
  search: Search,
  'chevron-right': ChevronRight,
  'chevron right': ChevronRight,
  'chevron-middle': ChevronRight,
  'chevron-up': ChevronUp,
  'chevron-down': ChevronDown,
  'chevron-left': ChevronLeft,
  'arrow-right': ArrowRight,
  'arrow-down': ArrowDown,
  'arrow-up-left': ArrowUpLeft,
  'arrow-up-right': ArrowUpRight,
  info: Info,
  upload: Upload,
  banknote: Banknote,
  warehouse: Warehouse,
  users: Users,
  user: User,
  house: House,
  menu: Menu,
  play: Play,
  webcam: Webcam,
  facebook: Facebook,
  linkedin: Linkedin,
  twitter: Twitter,
  whatsapp: WhatsappIcon,
  youtube: YoutubeIcon,
  tiktok: TiktokIcon,
  zap: Zap,
  mic: Mic,
  'book-open': BookOpen,
  mail: Mail,
  phone: Phone,
  pen: Pen,
  x: X,
  pyramid: Pyramid,
  list: List,
  link: Link,
  calendar: Calendar,
  'calendar-clock': CalendarClock,
  'map-pin': MapPin,
  lightbulb: Lightbulb,
  'hand-coins': HandCoins,
  check: Check,
  'mail-check': MailCheck,
  'heart-handshake': HeartHandshake,
  'users-round': UsersRound,
  'briefcase-business': BriefcaseBusiness,
  'chart-line': ChartLine,
  smile: Smile,
  shield: Shield,
  download: Download,
  'arrow-down-right': ArrowDownRight,
  smartphone: Smartphone,
  'arrow-left': ArrowLeft,
  instagram: Instagram,
  'refresh-cw': RefreshCw,
  newspaper: Newspaper,
  video: Video,
  'square-user-round': UserSquareIcon,
  'graduation-cap': GraduationCap,
  'circle-check': CircleCheck,
  'circle-x': CircleX,
  'rotate-ccw': RotateCcw,
  dot: Dot,
  podcast: Podcast,
  file: File,
  star: Star,
  plus: Plus,
  minus: Minus,
  gift: Gift,
  copy: Copy,
  'layers-3': Layers3,
  flag: Flag,
  // Image-legend swatches (WB-845). Unlike the rest of the set these carry
  // their own colour, because they restate a colour painted into the artwork
  // they annotate.
  'legend-box': LegendBoxIcon,
  'legend-line': LegendLineIcon,
  'legend-pin': LegendPinIcon,
} satisfies Record<string, React.ComponentType<LucideProps>>;

export type IconName = keyof typeof staticIcons;

const Icon = React.memo(
  ({
    name,
    size = 20,
    color = 'currentColor',
    className,
    ...props
  }: Omit<IconProps, 'ref'>) => {
    const normalizedName = name?.trim() as IconName;
    const IconComponent = staticIcons[normalizedName];
    const renamedProps = renameDomProps(props);
    if (!IconComponent) {
      // Names can come from Strapi, where an editor may set an unsupported or
      // misspelled value. Log for visibility but render nothing rather than
      // throwing, so a bad CMS value never breaks the page.
      console.error(
        `Icon "${normalizedName}" is not supported. Add it to the staticIcons map in Icon.tsx, or correct the icon name in the CMS.`,
      );
      return null;
    }

    return (
      <IconComponent
        className={`smzh-icon ${className}`}
        data-component-id="CO18"
        size={size}
        color={color}
        aria-hidden={
          !props['aria-label'] && !props['role'] ? 'true' : undefined
        }
        {...renamedProps}
      />
    );
  },
);

Icon.displayName = 'Icon';

export default Icon;
